/**
 * Owns the post-sign-in redirect round-trip.
 *
 * PrivateRoute sends an unauthenticated visitor to `/signin?re=<path><search>`
 * (src/components/routers/PrivateRoute/index.tsx). The sign-in panel stashes that
 * path while the visitor authenticates, then navigates to it afterwards.
 *
 * Keeping capture and consume in one module means the validation rule exists in
 * exactly one place. It previously lived in two, in the component that also owned
 * the widget.
 */
var KEY = 're';
/**
 * A safe destination is same-origin and path-absolute.
 *
 * The previous check was `re.indexOf('/') === 0`, which accepts `//evil.com` —
 * browsers read a leading `//` as protocol-relative, so
 * `window.location.assign('//evil.com')` leaves the site. That made
 * `/signin?re=//evil.com` an open redirect: a plausible-looking LTIAAS link that
 * lands the visitor on an attacker's page immediately after they type their
 * password. Requiring a single slash closes it.
 *
 * Backslashes are rejected too: some browsers normalise `/\evil.com` to `//`.
 */
var isSafePath = function (value) {
    return typeof value === 'string' && /^\/(?![/\\])/.test(value);
};
/**
 * Reads ?re= off the current URL and stores it for later. Safe to call more than
 * once (StrictMode double-mounts effects); it only ever writes.
 */
export var captureRedirect = function () {
    try {
        var re = new URLSearchParams(window.location.search).get(KEY);
        if (isSafePath(re)) {
            localStorage.setItem(KEY, re);
        }
    }
    catch (e) {
        /* storage unavailable — fall back to '/' at consume time */
    }
};
/**
 * Returns where to send the visitor and clears the stored value, so a stale entry
 * cannot hijack a later, unrelated sign-in. Always returns a safe path.
 */
export var consumeRedirect = function () {
    try {
        var to = localStorage.getItem(KEY);
        localStorage.removeItem(KEY);
        return isSafePath(to) ? to : '/';
    }
    catch (e) {
        return '/';
    }
};
/** Drops any stored destination without navigating. */
export var clearRedirect = function () {
    try {
        localStorage.removeItem(KEY);
    }
    catch (e) {
        /* nothing to do */
    }
};
